// AI Signal Card Component
import { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  Brain, 
  Target, 
  Shield, 
  Clock, 
  DollarSign,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Play,
  Pause,
  MoreHorizontal,
  ExternalLink
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AISignal } from "@/lib/ai-signals-api";
import { useSignalExecution } from "@/hooks/use-ai-signals";
import { useWallet } from "@/contexts/WalletContext";
import { toast } from "@/hooks/use-toast";

interface SignalCardProps {
  signal: AISignal;
  onExecute?: (signalId: string) => void;
  onCancel?: (signalId: string) => void;
  compact?: boolean;
}

export function SignalCard({ signal, onExecute, onCancel, compact = false }: SignalCardProps) {
  const [isExecuting, setIsExecuting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { executeSignal, cancelSignal } = useSignalExecution();
  const { publicKey, connected } = useWallet();

  const getSignalIcon = () => {
    switch (signal.signal_type) {
      case 'BUY':
        return <TrendingUp className="w-4 h-4" />;
      case 'SELL':
        return <TrendingDown className="w-4 h-4" />;
      case 'HOLD':
        return <Minus className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getSignalColor = () => {
    switch (signal.signal_type) {
      case 'BUY':
        return "text-success";
      case 'SELL':
        return "text-destructive";
      case 'HOLD':
        return "text-warning";
      default:
        return "text-muted-foreground";
    }
  };

  const getConfidenceColor = () => {
    switch (signal.confidence_level) {
      case 'HIGH':
        return "text-success";
      case 'MEDIUM':
        return "text-warning";
      case 'LOW':
        return "text-destructive";
      default:
        return "text-muted-foreground";
    }
  };

  const getRiskColor = (risk: number) => {
    if (risk <= 30) return "text-success";
    if (risk <= 60) return "text-warning";
    return "text-destructive";
  };

  const getTimeHorizonIcon = () => {
    switch (signal.time_horizon) {
      case 'IMMEDIATE':
        return <Clock className="w-3 h-3" />;
      case 'SHORT_TERM':
        return <Clock className="w-3 h-3" />;
      case 'MEDIUM_TERM':
        return <Target className="w-3 h-3" />;
      case 'LONG_TERM':
        return <Target className="w-3 h-3" />;
      default:
        return <Clock className="w-3 h-3" />;
    }
  };

  const handleExecuteClick = () => {
    if (!connected) {
      toast({
        title: "Wallet Not Connected",
        description: "Please connect your wallet to execute signals",
        variant: "destructive"
      });
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleExecuteConfirm = async () => {
    setShowConfirmDialog(false);
    setIsExecuting(true);
    try {
      // Get user wallet from context
      const walletAddress = publicKey || undefined;

      const result = await executeSignal(signal.id, walletAddress);
      if (result.success) {
        toast({
          title: "Signal Executed",
          description: `Successfully executed ${signal.signal_type} signal for ${signal.symbol}`,
        });
        onExecute?.(signal.id);
      } else {
        toast({
          title: "Execution Failed",
          description: result.error,
          variant: "destructive"
        });
      }
    } finally {
      setIsExecuting(false);
    }
  };

  const handleCancel = async () => {
    try {
      const result = await cancelSignal(signal.id);
      if (result.success) {
        toast({
          title: "Signal Cancelled",
          description: `Successfully cancelled ${signal.signal_type} signal for ${signal.symbol}`,
        });
        onCancel?.(signal.id);
      } else {
        toast({
          title: "Cancellation Failed",
          description: result.error,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error cancelling signal:', error);
    }
  };

  const profitLoss = signal.performance_metrics?.profit_loss;
  const accuracy = signal.performance_metrics?.accuracy;

  return (
    <Card className={cn(
      "relative overflow-hidden transition-all hover:shadow-lg",
      signal.status === 'ACTIVE' && "ring-1 ring-primary/20",
      signal.status === 'EXECUTED' && "bg-success/5",
      signal.status === 'EXPIRED' && "bg-muted/50",
      signal.status === 'CANCELLED' && "bg-destructive/5"
    )}>
      <CardHeader className={cn("pb-3", compact && "pb-2")}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={cn("flex items-center gap-1", getSignalColor())}>
              {getSignalIcon()}
              <span className="font-semibold">{signal.symbol}</span>
            </div>
            <Badge variant={signal.signal_type === 'BUY' ? 'default' : signal.signal_type === 'SELL' ? 'destructive' : 'secondary'}>
              {signal.signal_type}
            </Badge>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={getConfidenceColor()}>
              {signal.confidence_level}
            </Badge>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>AI Signal Details - {signal.symbol}</DialogTitle>
                </DialogHeader>
                <SignalDetailView signal={signal} />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>

      <CardContent className={cn("space-y-4", compact && "space-y-3")}>
        {/* Confidence and Risk */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Confidence</span>
              <span className={cn("text-xs font-mono", getConfidenceColor())}>
                {(signal.confidence_score * 100).toFixed(1)}%
              </span>
            </div>
            <Progress 
              value={signal.confidence_score * 100} 
              className="h-1"
            />
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Risk Score</span>
              <span className={cn("text-xs font-mono", getRiskColor(signal.risk_score))}>
                {signal.risk_score}/100
              </span>
            </div>
            <Progress 
              value={signal.risk_score} 
              className="h-1"
            />
          </div>
        </div>

        {/* Price Information */}
        {!compact && (
          <>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Current Price</span>
                <p className="font-mono">${signal.current_price.toFixed(6)}</p>
              </div>
              {signal.price_target && (
                <div>
                  <span className="text-muted-foreground">Price Target</span>
                  <p className="font-mono text-primary">${signal.price_target.toFixed(6)}</p>
                </div>
              )}
            </div>

            <Separator />

            {/* Model and Time Horizon */}
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1">
                <Brain className="w-3 h-3" />
                <span className="text-muted-foreground">{signal.model_name}</span>
              </div>
              <div className="flex items-center gap-1">
                {getTimeHorizonIcon()}
                <span className="text-muted-foreground">{signal.time_horizon.replace('_', ' ')}</span>
              </div>
            </div>

            {/* Performance Metrics */}
            {(profitLoss !== undefined || accuracy !== undefined) && (
              <div className="grid grid-cols-2 gap-4 text-xs">
                {profitLoss !== undefined && (
                  <div>
                    <span className="text-muted-foreground">P&L</span>
                    <p className={cn(
                      "font-mono",
                      profitLoss >= 0 ? "text-success" : "text-destructive"
                    )}>
                      {profitLoss >= 0 ? '+' : ''}{(profitLoss * 100).toFixed(2)}%
                    </p>
                  </div>
                )}
                {accuracy !== undefined && (
                  <div>
                    <span className="text-muted-foreground">Accuracy</span>
                    <p className="font-mono">{(accuracy * 100).toFixed(1)}%</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Status and Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {signal.status === 'ACTIVE' && (
              <Badge variant="outline" className="text-success border-success/20">
                <Activity className="w-3 h-3 mr-1" />
                Active
              </Badge>
            )}
            {signal.status === 'EXECUTED' && (
              <Badge variant="outline" className="text-success border-success/20">
                <CheckCircle className="w-3 h-3 mr-1" />
                Executed
              </Badge>
            )}
            {signal.status === 'EXPIRED' && (
              <Badge variant="outline" className="text-muted-foreground">
                <XCircle className="w-3 h-3 mr-1" />
                Expired
              </Badge>
            )}
            {signal.status === 'CANCELLED' && (
              <Badge variant="outline" className="text-destructive border-destructive/20">
                <XCircle className="w-3 h-3 mr-1" />
                Cancelled
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2">
            {signal.status === 'ACTIVE' && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExecuteClick}
                  disabled={isExecuting}
                >
                  {isExecuting ? (
                    <Activity className="w-3 h-3 animate-spin" />
                  ) : (
                    <Play className="w-3 h-3" />
                  )}
                  Execute
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCancel}
                >
                  <Pause className="w-3 h-3" />
                </Button>
              </>
            )}
            {signal.status === 'EXECUTED' && signal.executed_at && (
              <div className="text-xs text-muted-foreground">
                Executed {new Date(signal.executed_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      </CardContent>

      {/* Signal Execution Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning" />
              Confirm Signal Execution
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-secondary/50 rounded-lg">
              <h4 className="font-semibold mb-2">Signal Details</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Action:</span>
                  <Badge variant={signal.signal_type === 'BUY' ? 'default' : 'destructive'}>
                    {signal.signal_type} {signal.symbol}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span>Confidence:</span>
                  <span className={getConfidenceColor()}>
                    {(signal.confidence_score * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Risk Score:</span>
                  <span className={getRiskColor(signal.risk_score)}>
                    {signal.risk_score}/100
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Model:</span>
                  <span className="font-mono text-xs">{signal.model_name}</span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
              <h4 className="font-semibold mb-2 text-warning flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Risk Warnings
              </h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• AI signals are not guaranteed to be profitable</li>
                <li>• Market conditions can change rapidly</li>
                <li>• Position sizing is risk-adjusted but losses are possible</li>
                <li>• Transaction fees will apply</li>
                {signal.risk_score > 70 && (
                  <li className="text-destructive">• High risk signal - consider reducing position size</li>
                )}
              </ul>
            </div>

            <div className="p-4 bg-primary/5 border border-primary/10 rounded-lg">
              <h4 className="font-semibold mb-2">AI Reasoning</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {signal.reasoning}
              </p>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleExecuteConfirm}
              disabled={isExecuting}
              className="flex-1"
            >
              {isExecuting ? (
                <>
                  <Activity className="w-4 h-4 animate-spin mr-2" />
                  Executing...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Execute Signal
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Detailed view component for the dialog
function SignalDetailView({ signal }: { signal: AISignal }) {
  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h4 className="font-semibold mb-2">Signal Information</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Symbol:</span>
              <span className="font-mono">{signal.symbol}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type:</span>
              <span>{signal.signal_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Time Horizon:</span>
              <span>{signal.time_horizon.replace('_', ' ')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status:</span>
              <span>{signal.status}</span>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-semibold mb-2">Model Details</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Model:</span>
              <span>{signal.model_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Version:</span>
              <span>{signal.model_version}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Confidence:</span>
              <span className="font-mono">{(signal.confidence_score * 100).toFixed(1)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Risk Score:</span>
              <span className="font-mono">{signal.risk_score}/100</span>
            </div>
          </div>
        </div>
      </div>

      {/* Price Targets */}
      <div>
        <h4 className="font-semibold mb-2">Price Targets</h4>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Current Price</span>
            <p className="font-mono">${signal.current_price.toFixed(6)}</p>
          </div>
          {signal.entry_price && (
            <div>
              <span className="text-muted-foreground">Entry Price</span>
              <p className="font-mono">${signal.entry_price.toFixed(6)}</p>
            </div>
          )}
          {signal.price_target && (
            <div>
              <span className="text-muted-foreground">Target Price</span>
              <p className="font-mono text-primary">${signal.price_target.toFixed(6)}</p>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm mt-2">
          {signal.stop_loss && (
            <div>
              <span className="text-muted-foreground">Stop Loss</span>
              <p className="font-mono text-destructive">${signal.stop_loss.toFixed(6)}</p>
            </div>
          )}
          {signal.take_profit && (
            <div>
              <span className="text-muted-foreground">Take Profit</span>
              <p className="font-mono text-success">${signal.take_profit.toFixed(6)}</p>
            </div>
          )}
        </div>
      </div>

      {/* AI Reasoning */}
      <div>
        <h4 className="font-semibold mb-2">AI Reasoning</h4>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {signal.reasoning}
        </p>
      </div>

      {/* Technical Indicators */}
      {signal.technical_indicators && (
        <div>
          <h4 className="font-semibold mb-2">Technical Indicators</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {signal.technical_indicators.rsi && (
              <div>
                <span className="text-muted-foreground">RSI</span>
                <p className="font-mono">{signal.technical_indicators.rsi.toFixed(2)}</p>
              </div>
            )}
            {signal.technical_indicators.macd && (
              <div>
                <span className="text-muted-foreground">MACD</span>
                <p className="font-mono">{signal.technical_indicators.macd.toFixed(4)}</p>
              </div>
            )}
            {signal.technical_indicators.bollinger_position && (
              <div>
                <span className="text-muted-foreground">Bollinger Position</span>
                <p className="font-mono">{(signal.technical_indicators.bollinger_position * 100).toFixed(1)}%</p>
              </div>
            )}
            {signal.technical_indicators.volume_analysis && (
              <div>
                <span className="text-muted-foreground">Volume Analysis</span>
                <p>{signal.technical_indicators.volume_analysis}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Portfolio Impact */}
      {signal.portfolio_impact && (
        <div>
          <h4 className="font-semibold mb-2">Portfolio Impact</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Recommended Allocation</span>
              <p className="font-mono">{(signal.portfolio_impact.recommended_allocation * 100).toFixed(1)}%</p>
            </div>
            <div>
              <span className="text-muted-foreground">Risk Adjustment</span>
              <p className="font-mono">{(signal.portfolio_impact.risk_adjustment * 100).toFixed(1)}%</p>
            </div>
            <div>
              <span className="text-muted-foreground">Diversification Score</span>
              <p className="font-mono">{(signal.portfolio_impact.diversification_score * 100).toFixed(1)}%</p>
            </div>
          </div>
        </div>
      )}

      {/* Market Sentiment */}
      {signal.market_sentiment && (
        <div>
          <h4 className="font-semibold mb-2">Market Sentiment</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Overall</span>
              <Badge variant="outline" className={
                signal.market_sentiment.overall === 'BULLISH' ? 'text-success' :
                signal.market_sentiment.overall === 'BEARISH' ? 'text-destructive' :
                'text-warning'
              }>
                {signal.market_sentiment.overall}
              </Badge>
            </div>
            {signal.market_sentiment.fear_greed_index && (
              <div>
                <span className="text-muted-foreground">Fear & Greed</span>
                <p className="font-mono">{signal.market_sentiment.fear_greed_index}</p>
              </div>
            )}
            {signal.market_sentiment.social_sentiment && (
              <div>
                <span className="text-muted-foreground">Social Sentiment</span>
                <p className="font-mono">{(signal.market_sentiment.social_sentiment * 100).toFixed(1)}%</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Performance Metrics */}
      {signal.performance_metrics && (
        <div>
          <h4 className="font-semibold mb-2">Performance Metrics</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {signal.performance_metrics.profit_loss !== undefined && (
              <div>
                <span className="text-muted-foreground">Profit/Loss</span>
                <p className={cn(
                  "font-mono",
                  signal.performance_metrics.profit_loss >= 0 ? "text-success" : "text-destructive"
                )}>
                  {signal.performance_metrics.profit_loss >= 0 ? '+' : ''}
                  {(signal.performance_metrics.profit_loss * 100).toFixed(2)}%
                </p>
              </div>
            )}
            {signal.performance_metrics.accuracy !== undefined && (
              <div>
                <span className="text-muted-foreground">Accuracy</span>
                <p className="font-mono">{(signal.performance_metrics.accuracy * 100).toFixed(1)}%</p>
              </div>
            )}
            {signal.performance_metrics.max_drawdown !== undefined && (
              <div>
                <span className="text-muted-foreground">Max Drawdown</span>
                <p className="font-mono text-destructive">
                  {(signal.performance_metrics.max_drawdown * 100).toFixed(2)}%
                </p>
              </div>
            )}
            {signal.performance_metrics.sharpe_ratio !== undefined && (
              <div>
                <span className="text-muted-foreground">Sharpe Ratio</span>
                <p className="font-mono">{signal.performance_metrics.sharpe_ratio.toFixed(2)}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Timestamps */}
      <div>
        <h4 className="font-semibold mb-2">Timeline</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Created:</span>
            <span className="font-mono">{new Date(signal.created_at).toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Updated:</span>
            <span className="font-mono">{new Date(signal.updated_at).toLocaleString()}</span>
          </div>
          {signal.executed_at && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Executed:</span>
              <span className="font-mono">{new Date(signal.executed_at).toLocaleString()}</span>
            </div>
          )}
          {signal.expires_at && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Expires:</span>
              <span className="font-mono">{new Date(signal.expires_at).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}